import { Link, useLocation } from "react-router-dom";
import { LOCALES, LOCALE_ORDER, marketingLocaleForPath, type Locale } from "@/config/locales";

/**
 * Public marketing language selector.
 *
 * Shown ONLY on localized pairs (homepage, pricing), where both localized versions
 * actually exist (`/` and `/es`). Real links, no JavaScript-only switching, no
 * browser-language or geolocation redirects, and never rendered inside the
 * authenticated application.
 */
const LOCALIZED_PAIRS: Record<string, Record<Locale, string>> = {
  "/": { en: "/", es: "/es" },
  "/es": { en: "/", es: "/es" },
  "/pricing": { en: "/pricing", es: "/es/pricing" },
  "/es/pricing": { en: "/pricing", es: "/es/pricing" },
};

export function useLocalizedPair() {
  const { pathname } = useLocation();
  const key = pathname.replace(/\/+$/, "") || "/";
  return { pair: LOCALIZED_PAIRS[key], current: marketingLocaleForPath(key) };
}

export function LanguageSwitcher({ className }: { className?: string }) {
  const { pair, current } = useLocalizedPair();
  if (!pair) return null;

  return (
    <nav aria-label="Language" className={`flex items-center gap-1 ${className ?? ""}`}>
      {LOCALE_ORDER.map((code, i) => {
        const active = code === current;
        return (
          <span key={code} className="flex items-center gap-1">
            {i > 0 && (
              <span aria-hidden="true" className="text-border-strong">
                |
              </span>
            )}
            <Link
              to={pair[code]}
              hrefLang={code}
              lang={code}
              aria-current={active ? "true" : undefined}
              className={`rounded px-1 text-xs font-medium transition-colors ${
                active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="sr-only">{LOCALES[code].label}</span>
              <span aria-hidden="true">{LOCALES[code].short}</span>
            </Link>
          </span>
        );
      })}
    </nav>
  );
}
